import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	Divider,
	Drawer,
	FormControl,
	FormControlLabel,
	Grid,
	IconButton,
	InputAdornment,
	InputLabel,
	LinearProgress,
	MenuItem,
	Select,
	Stack,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import { ICustomerCorpoDTO, ICustomerLeisureDTO } from 'features/customers/types';
import { IPassenger, IServices, SegmentEnum } from 'features/salesOrder/types';
import { countriesWithAgencyConfig } from 'features/auth/constants';
import { ICreateInvoicePayload, IInvoice, IInvoiceItem, IInvoiceReceipt, InvoiceTypeEnum } from '../../types';
import { createInvoice, getInvoiceReceipts } from '../../services';
import { computeBillableServices } from '../../billingEngine';
import { formatAmount, PLATFORM_LABEL, todayIso } from '../../uiHelpers';
import { getPassengerDetails } from 'features/passengers/services';
import { extractErrorMessage } from '../../../quotation/helpers';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import { getAgencyIdByCountryCode } from '../../../auth/helpers';
import { useResolvedDueDate } from 'features/creditRules/hooks/useCreditRules';
import { DueDateRuleNotice } from 'features/creditRules/components/DueDateRuleNotice';

export interface InvoiceableParty {
	id: string;
	name: string;
	isPhysical: boolean;
}

export interface Props {
	open: boolean;
	onClose: () => void;
	saleOrderId: string;
	invoiceables: InvoiceableParty[];
	creditNoteFor?: IInvoice;
	onCreated: (invoice: IInvoice) => void;
	services: IServices[];
	existingInvoices: IInvoice[];
	customer?: ICustomerLeisureDTO | ICustomerCorpoDTO;
	/** Todos los pax de la orden, para resolver nombres en la adenda. */
	passengers?: IPassenger[];
	segmentType?: SegmentEnum;
	/** Referencia del cliente (OC / identificador de venta) de la orden. */
	clientReference?: string;
	/** Fecha de inicio del viaje (para el vencimiento Leisure −45 en PY/TN). */
	travelDepartureDate?: string;
}

interface ItemRow {
	serviceId?: string;
	description: string;
	/** Detalle informativo del servicio: hotel, ruta, fechas, pax. Solo lectura. */
	adenda?: string;
	unitPrice: number;
	taxRate: number;
	/**
	 * Proporción del importe que es base imponible (default 1).
	 * Vuelos PY: 0.25 → IVA 10% s/ 25% del pasaje = 2.5% efectivo del total.
	 */
	taxableBaseRatio?: number;
	selected: boolean;
	fullyBilled?: boolean;
	/** Total original del servicio en la moneda actual de la factura. */
	serviceTotalSaleAmount?: number;
	/** Monto ya facturado en la moneda actual de la factura. */
	serviceAlreadyBilled?: number;
}

const COLOMBIA_AGENCY_ID = 2;
const PARAGUAY_AGENCY_ID = 3;
const TN_ITAU_PY_AGENCY_ID = 5;
const MAIN_CURRENCY = 'USD';

/** Leisure en PY/TN: la factura crédito vence 45 días antes del inicio del viaje. */
const LEISURE_CREDIT_DAYS_BEFORE_DEPARTURE = 45;

const round2 = (v: number): number => Number((Number(v) || 0).toFixed(2));

const normalizeCurrency = (value: string): string => value.trim().toUpperCase();

const addDays = (dateIso: string, days: number): string => {
	const d = new Date(`${dateIso}T12:00:00`);
	d.setDate(d.getDate() + days);
	return d.toISOString().slice(0, 10);
};

/**
 * exchangeRate = moneda local por 1 USD.
 * Ejemplo COP: 600000 COP / 3760 = 159.57 USD.
 */
const toMainCurrency = (amount: number, currency: string, exchangeRate: number): number => {
	if (normalizeCurrency(currency) === MAIN_CURRENCY) return round2(amount);
	if (!exchangeRate) return 0;
	return round2(amount / exchangeRate);
};

/**
 * Calcula cuánto cubre un recibo en la moneda de la factura.
 * Debe estar alineado con backend.
 */
const coverageOfReceipt = (receipt: IInvoiceReceipt, invoiceCurrency: string): number => {
	const receiptCurrency = normalizeCurrency(receipt.currency);
	const cleanInvoiceCurrency = normalizeCurrency(invoiceCurrency);

	if (receiptCurrency === cleanInvoiceCurrency) {
		return round2(receipt.transactionAmount);
	}

	if (cleanInvoiceCurrency === MAIN_CURRENCY) {
		return round2(receipt.amountInMainCurrency);
	}

	if (
		receipt.transactionAmountInLocalCurrency !== undefined &&
		receipt.transactionAmountInLocalCurrency !== null &&
		receipt.transactionAmountInLocalCurrency > 0
	) {
		return round2(receipt.transactionAmountInLocalCurrency);
	}

	if (
		receipt.rateToConvertToLocalCurrency !== undefined &&
		receipt.rateToConvertToLocalCurrency !== null &&
		receipt.rateToConvertToLocalCurrency > 0
	) {
		return round2(receipt.amountInMainCurrency * receipt.rateToConvertToLocalCurrency);
	}

	return 0;
};

/**
 * computeBillableServices devuelve montos en moneda principal.
 * Si la factura se hace en COP, usamos el monto local proporcional del servicio.
 */
const serviceAmountForCurrency = (
	amountInMainCurrency: number,
	serviceTotalMainCurrency: number,
	serviceTotalLocalCurrency: number | undefined,
	targetCurrency: string,
): number => {
	if (normalizeCurrency(targetCurrency) === MAIN_CURRENCY) {
		return round2(amountInMainCurrency);
	}

	if (!serviceTotalLocalCurrency || serviceTotalLocalCurrency <= 0 || serviceTotalMainCurrency <= 0) {
		return round2(amountInMainCurrency);
	}

	return round2(serviceTotalLocalCurrency * (amountInMainCurrency / serviceTotalMainCurrency));
};

const isCorpoCustomer = (c?: ICustomerLeisureDTO | ICustomerCorpoDTO): c is ICustomerCorpoDTO =>
	Boolean(c && 'daysOfCredit' in c);

const emptyRow = (): ItemRow => ({ description: '', unitPrice: 0, taxRate: 0, selected: true });

export const InvoiceDrawer = ({
	open,
	onClose,
	saleOrderId,
	invoiceables,
	creditNoteFor,
	onCreated,
	services,
	existingInvoices,
	customer,
	passengers,
	segmentType,
	clientReference,
	travelDepartureDate,
}: Props): JSX.Element => {
	const isCreditNote = Boolean(creditNoteFor);
	const invoiceablesCount = invoiceables.length;
	const { auth } = useSelector((state: RootState) => state);
	const agencyId = getAgencyIdByCountryCode(auth.country);
	const defaultCurrency = agencyId === COLOMBIA_AGENCY_ID ? 'COP' : 'USD';

	const [type, setType] = useState<InvoiceTypeEnum>(InvoiceTypeEnum.Credit);
	const [passengerId, setPassengerId] = useState('');
	const [currency, setCurrency] = useState(defaultCurrency);
	const [issueDate, setIssueDate] = useState(todayIso());
	const [dueDate, setDueDate] = useState(todayIso());
	const [exchangeRate, setExchangeRate] = useState<number>(1);
	const [rows, setRows] = useState<ItemRow[]>([emptyRow()]);
	const [externalReference, setExternalReference] = useState('');
	const [pdfUrl, setPdfUrl] = useState('');
	const [notes, setNotes] = useState('');

	const [freeReceipts, setFreeReceipts] = useState<IInvoiceReceipt[]>([]);
	const [selectedReceipts, setSelectedReceipts] = useState<Set<string>>(new Set());
	const [receiptsLoading, setReceiptsLoading] = useState(false);

	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	/**
	 * Billing settings + saldo general de cobro (USD) del invoiceable.
	 * El límite de crédito se compara contra la exposición real del cliente
	 * (venta − cobrado en todas sus órdenes, facturado o no).
	 */
	const [invoiceableBilling, setInvoiceableBilling] = useState<{
		deadlineDay?: number;
		maxCredit?: number;
		requiresPurchaseOrder?: boolean;
		clientNotes?: string;
		pendingUsd: number;
	} | null>(null);

	useEffect(() => {
		if (!open || isCreditNote || !passengerId) {
			setInvoiceableBilling(null);
			return;
		}

		let cancelled = false;

		getPassengerDetails(passengerId)
			.then((detailResponse) => {
				if (cancelled) return;

				setInvoiceableBilling({
					deadlineDay: detailResponse.data.invoiceReceptionDeadlineDay,
					maxCredit: detailResponse.data.maximumCreditAmount,
					requiresPurchaseOrder: detailResponse.data.requiresPurchaseOrder,
					clientNotes: detailResponse.data.notes,
					pendingUsd: round2(detailResponse.data.pendingPayAmount ?? 0),
				});
			})
			.catch(() => {
				if (!cancelled) setInvoiceableBilling(null);
			});

		return () => {
			cancelled = true;
		};
	}, [open, isCreditNote, passengerId]);

	const passengerNameById = useMemo((): Record<string, string> => {
		const map: Record<string, string> = {};
		for (const p of passengers ?? []) {
			map[p.keyId] = `${p.name} ${p.lastName}`.trim();
		}
		return map;
	}, [passengers]);

	const billableServices = useMemo(
		() => computeBillableServices(services, existingInvoices, { passengerNameById, agencyId }),
		[services, existingInvoices, passengerNameById, agencyId],
	);

	/**
	 * TRM implícita de la orden: los servicios traen monto en moneda local y en USD,
	 * así que el TC se deduce solo (sum local / sum USD) y se precarga en el drawer.
	 */
	const impliedExchangeRate = useMemo((): number | null => {
		let localSum = 0;
		let mainSum = 0;

		for (const s of billableServices) {
			if (s.localCurrencyAmount && s.localCurrencyAmount > 0 && s.totalSaleAmount > 0) {
				localSum += s.localCurrencyAmount;
				mainSum += s.totalSaleAmount;
			}
		}

		if (localSum <= 0 || mainSum <= 0) return null;
		return round2(localSum / mainSum);
	}, [billableServices]);

	const corpoCreditDays = isCorpoCustomer(customer) ? customer.daysOfCredit : 0;
	const contadoAvailable = !receiptsLoading && freeReceipts.length > 0;

	/**
	 * Leisure en BU PY/TN: la factura crédito vence 45 días antes del inicio
	 * del viaje; si eso ya pasó (viaje a menos de 45 días), vence hoy.
	 */
	const leisureCreditDueDate = useMemo((): string | null => {
		if (segmentType !== SegmentEnum.LEISURE || !travelDepartureDate) return null;
		if (agencyId !== PARAGUAY_AGENCY_ID && agencyId !== TN_ITAU_PY_AGENCY_ID) return null;

		const candidate = addDays(travelDepartureDate.slice(0, 10), -LEISURE_CREDIT_DAYS_BEFORE_DEPARTURE);
		const today = todayIso();
		return candidate < today ? today : candidate;
	}, [segmentType, travelDepartureDate, agencyId]);

	/**
	 * Condición de crédito del cliente (Support API). Es la fuente de verdad del
	 * vencimiento: gana sobre `daysOfCredit` y sobre la regla de Leisure PY/TN,
	 * que quedan como fallback para los clientes que todavía no tienen condición
	 * cargada.
	 */
	const { data: creditRule, loading: creditRuleLoading } = useResolvedDueDate({
		passengerId,
		fechaFactura: issueDate,
		fechaSalidaPrimerServicio: travelDepartureDate?.slice(0, 10),
		enabled: open && type === InvoiceTypeEnum.Credit,
	});
	const ruleDueDate =
		creditRule && !creditRule.sinCondicion && !creditRule.resultado?.requiereRevisionManual
			? creditRule.resultado?.fechaVencFinal ?? null
			: null;

	const applyDueDateFromCredit = useCallback(
		(baseDate: string): void => {
			if (type === InvoiceTypeEnum.Cash) return;
			// La condición del cliente se aplica sola vía el efecto de abajo
			// (depende del cálculo del backend, que es asincrónico).
			if (ruleDueDate) return;
			if (corpoCreditDays > 0) setDueDate(addDays(baseDate, corpoCreditDays));
		},
		[corpoCreditDays, type, ruleDueDate],
	);

	/**
	 * Prellena el vencimiento cuando llega (o cambia) la condición del cliente.
	 * Solo pisa el campo si el usuario todavía no lo tocó a mano: una vez que
	 * editó la fecha, mandamos nosotros no — se le avisa la divergencia y puede
	 * restaurarla con un botón.
	 */
	const [dueDateTouched, setDueDateTouched] = useState(false);
	useEffect(() => {
		// Solo en facturas de crédito: en Contado el vencimiento es la emisión, y
		// una nota de crédito no lleva condición del cliente.
		if (!ruleDueDate || dueDateTouched || type !== InvoiceTypeEnum.Credit) return;
		setDueDate(ruleDueDate);
	}, [ruleDueDate, dueDateTouched, type]);

	/**
	 * En CONTADO, si los servicios superan los recibos, bajamos proporcionalmente
	 * los importes seleccionados para que entren en la cobertura.
	 */
	const capRowsToAmount = useCallback((rowList: ItemRow[], cap: number): ItemRow[] => {
		const selectedTotal = rowList.filter((r) => r.selected).reduce((acc, r) => acc + r.unitPrice, 0);
		if (selectedTotal <= cap + 0.01) return rowList;

		const ratio = cap / selectedTotal;
		return rowList.map((r) => (r.selected ? { ...r, unitPrice: round2(r.unitPrice * ratio) } : r));
	}, []);

	const buildNotesFromBillable = useCallback(
		(rowList: ItemRow[]): string => {
			const lines = rowList
				.filter((r) => r.selected && r.adenda)
				.map((r) => {
					const pctStr =
						r.serviceTotalSaleAmount && r.serviceTotalSaleAmount > 0
							? ` (${Math.round((r.unitPrice / r.serviceTotalSaleAmount) * 100)}% del servicio)`
							: '';

					return `${r.description}: ${r.adenda}${pctStr}`;
				});

			if (clientReference?.trim()) {
				lines.unshift(`Ref. cliente: ${clientReference.trim()}`);
			}

			return lines.join('\n');
		},
		[clientReference],
	);

	const buildRowsFromBillable = useCallback(
		(mode: 'pending' | 'equitative', targetCurrency = currency): ItemRow[] => {
			const billable = billableServices.filter((s) => s.totalSaleAmount > 0);
			if (billable.length === 0) return [emptyRow()];

			return billable.map((s) => {
				const pendingMainAmount =
					mode === 'equitative' && invoiceablesCount > 1
						? round2(s.pendingAmount / invoiceablesCount)
						: s.pendingAmount;

				const unitPrice = serviceAmountForCurrency(
					pendingMainAmount,
					s.totalSaleAmount,
					s.localCurrencyAmount,
					targetCurrency,
				);

				const serviceTotalSaleAmount = serviceAmountForCurrency(
					s.totalSaleAmount,
					s.totalSaleAmount,
					s.localCurrencyAmount,
					targetCurrency,
				);

				const serviceAlreadyBilled = serviceAmountForCurrency(
					s.alreadyBilledAmount,
					s.totalSaleAmount,
					s.localCurrencyAmount,
					targetCurrency,
				);

				return {
					serviceId: s.serviceId,
					description: s.label,
					adenda: s.adenda || undefined,
					unitPrice,
					taxRate: s.suggestedTaxRate,
					taxableBaseRatio: s.suggestedTaxableBaseRatio,
					selected: s.pendingAmount > 0,
					fullyBilled: s.pendingAmount <= 0,
					serviceTotalSaleAmount,
					serviceAlreadyBilled,
				};
			});
		},
		[billableServices, invoiceablesCount, currency],
	);

	useEffect(() => {
		if (!open || isCreditNote) return;

		setReceiptsLoading(true);
		setFreeReceipts([]);

		getInvoiceReceipts({ saleOrderId, unlinked: true })
			.then((receipts) => {
				setFreeReceipts(receipts);
				setSelectedReceipts(new Set(receipts.map((r) => r.id)));

				if (receipts.length > 0 && corpoCreditDays <= 0) {
					setType(InvoiceTypeEnum.Cash);

					const totalReceipts = receipts.reduce((acc, r) => acc + coverageOfReceipt(r, defaultCurrency), 0);

					setRows((prev) => {
						const capped = capRowsToAmount(prev, totalReceipts);
						setNotes(buildNotesFromBillable(capped));
						return capped;
					});
				} else {
					setType(InvoiceTypeEnum.Credit);
				}
			})
			.catch(() => setFreeReceipts([]))
			.finally(() => setReceiptsLoading(false));
	}, [open, isCreditNote, saleOrderId, corpoCreditDays]);

	useEffect(() => {
		if (!open) return;

		setError(null);
		setSubmitting(false);
		setExternalReference('');
		setPdfUrl('');
		setNotes('');

		const newCurrency = agencyId === COLOMBIA_AGENCY_ID ? 'COP' : 'USD';
		setCurrency(newCurrency);
		setExchangeRate(newCurrency !== MAIN_CURRENCY && impliedExchangeRate ? impliedExchangeRate : 1);

		if (isCreditNote && creditNoteFor) {
			setType(InvoiceTypeEnum.CreditNote);
			setPassengerId('');
			setCurrency(creditNoteFor.currency);
			setExchangeRate(creditNoteFor.exchangeRate ?? 1);
			setIssueDate(todayIso());
			setDueDate(todayIso());

			setRows(
				creditNoteFor.items.map((item) => ({
					serviceId: item.serviceId,
					description: `Anulación · ${item.description}`,
					// El importe de fila es el total final del ítem (IVA incluido).
					unitPrice: -Math.abs(item.total),
					taxRate: item.taxRate,
					selected: true,
				})),
			);
		} else {
			setPassengerId(invoiceables[0]?.id ?? '');
			setIssueDate(todayIso());
			// El vencimiento vuelve a ser "no tocado": si el cliente tiene
			// condición de crédito, la va a completar el efecto correspondiente.
			setDueDateTouched(false);

			if (corpoCreditDays > 0) {
				setType(InvoiceTypeEnum.Credit);
				setDueDate(addDays(todayIso(), corpoCreditDays));
			} else if (leisureCreditDueDate) {
				setDueDate(leisureCreditDueDate);
			} else {
				setDueDate(todayIso());
			}

			const initialRows = buildRowsFromBillable('pending', newCurrency);
			setRows(initialRows);
			setNotes(buildNotesFromBillable(initialRows));
		}
	}, [open]);

	useEffect(() => {
		if (type === InvoiceTypeEnum.Cash) setDueDate(issueDate);
	}, [type, issueDate]);

	const selectedRows = useMemo(() => rows.filter((r) => r.selected), [rows]);

	/**
	 * IVA "hacia adentro": el importe cargado por el operador es el FINAL del ítem.
	 * La base imponible se calcula descontando el IVA del importe, no sumándolo.
	 * El backend computa subtotal = unitPrice × qty y taxAmount = taxableBase × rate,
	 * por eso se envía unitPrice = neto (importe − IVA) para que su total = importe cargado.
	 *
	 * Con base reducida (vuelos PY: 25%) la base es una proporción del importe final
	 * y el IVA se calcula sobre esa base (10% s/ 25% = 2.5% efectivo del total).
	 */
	const computedItems = useMemo(
		(): Omit<IInvoiceItem, 'id' | 'invoiceId'>[] =>
			selectedRows.map((row) => {
				const total = round2(row.unitPrice);
				const baseRatio = row.taxableBaseRatio ?? 1;

				let taxableBase: number;
				let taxAmount: number;
				let netUnitPrice: number;

				if (row.taxRate > 0 && baseRatio < 1) {
					taxableBase = round2(total * baseRatio);
					taxAmount = round2(taxableBase * (row.taxRate / 100));
					netUnitPrice = round2(total - taxAmount);
				} else {
					const rawBase = row.taxRate > 0 ? total / (1 + row.taxRate / 100) : total;
					taxAmount = round2(rawBase * (row.taxRate / 100));
					netUnitPrice = round2(total - taxAmount);
					taxableBase = Math.min(round2(rawBase), netUnitPrice);
				}

				const subtotal = netUnitPrice;

				return {
					description: row.description,
					serviceId: row.serviceId,
					unitPrice: netUnitPrice,
					quantity: 1,
					subtotal,
					taxableBase,
					taxRate: row.taxRate,
					taxAmount,
					total,
					subtotalInMainCurrency: toMainCurrency(subtotal, currency, exchangeRate),
					totalInMainCurrency: toMainCurrency(total, currency, exchangeRate),
				};
			}),
		[selectedRows, currency, exchangeRate],
	);

	const totals = useMemo(() => {
		const subtotal = round2(computedItems.reduce((acc, i) => acc + i.subtotal, 0));
		const taxAmount = round2(computedItems.reduce((acc, i) => acc + i.taxAmount, 0));
		const total = round2(computedItems.reduce((acc, i) => acc + i.total, 0));

		return { subtotal, taxAmount, total };
	}, [computedItems]);

	/** Avisos no bloqueantes según los billing settings del cliente. */
	const billingWarnings = useMemo((): string[] => {
		if (!invoiceableBilling) return [];

		const warnings: string[] = [];
		const todayDayOfMonth = new Date().getDate();

		if (invoiceableBilling.deadlineDay && todayDayOfMonth > invoiceableBilling.deadlineDay) {
			warnings.push(
				`El cliente recibe facturas hasta el día ${invoiceableBilling.deadlineDay} de cada mes (hoy es ${todayDayOfMonth}). Coordiná antes de emitir.`,
			);
		}

		if (invoiceableBilling.requiresPurchaseOrder && !clientReference?.trim()) {
			warnings.push(
				'El cliente exige orden de compra (OC) y la orden no tiene referencia de cliente cargada. Cargala en General antes de facturar.',
			);
		}

		// La exposición no cambia por facturar (la venta ya la generó): se compara
		// el saldo general de cobro del cliente contra su tope, sin sumar esta factura.
		if (invoiceableBilling.maxCredit != null && invoiceableBilling.pendingUsd > invoiceableBilling.maxCredit) {
			warnings.push(
				`El cliente supera su límite de crédito: saldo pendiente ${formatAmount(
					invoiceableBilling.pendingUsd,
					'USD',
				)} / tope ${formatAmount(invoiceableBilling.maxCredit, 'USD')} (todas sus órdenes).`,
			);
		}

		return warnings;
	}, [invoiceableBilling, clientReference]);

	const projectedCoverage = useMemo(() => {
		if (type !== InvoiceTypeEnum.Cash) return 0;

		return round2(
			freeReceipts
				.filter((r) => selectedReceipts.has(r.id))
				.reduce((acc, r) => acc + coverageOfReceipt(r, currency), 0),
		);
	}, [type, freeReceipts, selectedReceipts, currency]);

	const maxContadoAmount = useMemo(
		() => round2(freeReceipts.reduce((acc, r) => acc + coverageOfReceipt(r, currency), 0)),
		[freeReceipts, currency],
	);

	const updateRow = (index: number, patch: Partial<ItemRow>): void => {
		setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
	};

	const toggleReceipt = (id: string): void => {
		setSelectedReceipts((prev) => {
			const next = new Set(prev);
			next.has(id) ? next.delete(id) : next.add(id);
			return next;
		});
	};

	const handleCurrencyChange = (newCurrency: string): void => {
		setCurrency(newCurrency);

		if (normalizeCurrency(newCurrency) === MAIN_CURRENCY) {
			setExchangeRate(1);
		} else if (impliedExchangeRate) {
			setExchangeRate(impliedExchangeRate);
		}

		if (!isCreditNote) {
			let nextRows = buildRowsFromBillable('pending', newCurrency);

			if (type === InvoiceTypeEnum.Cash && freeReceipts.length > 0) {
				const cap = freeReceipts.reduce((acc, r) => acc + coverageOfReceipt(r, newCurrency), 0);
				nextRows = capRowsToAmount(nextRows, cap);
			}

			setRows(nextRows);
			setNotes(buildNotesFromBillable(nextRows));
		}
	};

	const handleTypeChange = (newType: InvoiceTypeEnum): void => {
		if (newType === InvoiceTypeEnum.Cash && !contadoAvailable) return;

		setType(newType);

		if (newType === InvoiceTypeEnum.Credit && corpoCreditDays <= 0 && leisureCreditDueDate) {
			setDueDate(leisureCreditDueDate);
		}

		if (newType === InvoiceTypeEnum.Cash && freeReceipts.length > 0) {
			const totalReceipts = freeReceipts.reduce((acc, r) => acc + coverageOfReceipt(r, currency), 0);

			setRows((prev) => {
				const capped = capRowsToAmount(prev, totalReceipts);
				setNotes(buildNotesFromBillable(capped));
				return capped;
			});
		}
	};

	const handleSubmit = async (): Promise<void> => {
		if (!isCreditNote && !passengerId) {
			setError('Seleccioná un pasajero/empresa a facturar.');
			return;
		}

		if (computedItems.length === 0 || computedItems.every((i) => i.total === 0)) {
			setError('Seleccioná al menos un ítem con importe.');
			return;
		}

		/**
		 * Con moneda local, un TC en 1 (o menos) es siempre un error de carga:
		 * el equivalente en USD saldría igual al monto local y la API lo rechaza.
		 */
		if (normalizeCurrency(currency) !== MAIN_CURRENCY && exchangeRate <= 1) {
			setError(
				`El tipo de cambio ${currency}/USD no puede ser ${exchangeRate}. Cargá el TC real (cuántos ${currency} equivalen a 1 USD).`,
			);
			return;
		}

		/**
		 * Backend exige que CONTADO quede cubierto exactamente por los recibos enviados.
		 */
		if (type === InvoiceTypeEnum.Cash) {
			if (selectedReceipts.size === 0) {
				setError('Seleccioná al menos un anticipo para crear una factura contado.');
				return;
			}

			if (Math.abs(totals.total - projectedCoverage) > 0.01) {
				setError(
					`Para factura Contado el total (${formatAmount(
						totals.total,
						currency,
					)}) debe coincidir con los anticipos seleccionados (${formatAmount(projectedCoverage, currency)}).`,
				);
				return;
			}
		}

		const resolvedPassengerName =
			!isCreditNote && passengerId
				? invoiceables.find((p) => p.id === passengerId)?.name
				: creditNoteFor?.passengerName;

		const payload: ICreateInvoicePayload = {
			saleOrderId,
			type,
			passengerId: isCreditNote && creditNoteFor ? creditNoteFor.id : passengerId,
			passengerName: resolvedPassengerName,
			currency,
			issueDate,
			dueDate,
			exchangeRate: currency === 'USD' ? undefined : exchangeRate,
			items: computedItems,
			externalReference: externalReference.trim() || undefined,
			pdfUrl: pdfUrl.trim() || undefined,
			notes: notes.trim() || undefined,
			creditNoteForInvoiceId: isCreditNote && creditNoteFor ? creditNoteFor.id : undefined,
			receiptIds: type === InvoiceTypeEnum.Cash && selectedReceipts.size > 0 ? Array.from(selectedReceipts) : undefined,
		};

		setSubmitting(true);
		setError(null);

		try {
			const created = await createInvoice(payload);
			onCreated(created);
			onClose();
		} catch (err) {
			setError(extractErrorMessage(err, 'No se pudo crear la factura.'));
		} finally {
			setSubmitting(false);
		}
	};

	const pendingServiceCount = billableServices.filter((s) => s.pendingAmount > 0).length;
	const hasBillableServices = services.length > 0;

	const totalPendingAllServices = round2(
		billableServices.reduce(
			(acc, s) => acc + serviceAmountForCurrency(s.pendingAmount, s.totalSaleAmount, s.localCurrencyAmount, currency),
			0,
		),
	);

	const contadoIsCapped =
		type === InvoiceTypeEnum.Cash && maxContadoAmount > 0 && totalPendingAllServices > maxContadoAmount + 0.01;

	const localCurrencyLabel = countriesWithAgencyConfig.find((c) => c.agencyId === agencyId)?.id ?? 'USD';

	if (isCreditNote && creditNoteFor) {
		return (
			<Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 560 } } }}>
				<Box sx={{ p: 3, pb: 6 }}>
					<Typography variant="h6" sx={{ mb: 0.5 }}>
						Nota de crédito
					</Typography>

					<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
						Se generará una NC por el total de la factura {creditNoteFor.invoiceNumber}. La factura original se marcará
						como acreditada recién cuando la NC sea emitida.
					</Typography>

					<Box sx={{ bgcolor: 'error.50', border: 1, borderColor: 'error.200', borderRadius: 1, p: 2, mb: 3 }}>
						<Stack direction="row" justifyContent="space-between" alignItems="center">
							<Box>
								<Typography variant="subtitle2">{creditNoteFor.invoiceNumber}</Typography>
								<Typography variant="body2" color="text.secondary">
									{creditNoteFor.passengerName}
								</Typography>
							</Box>
							<Typography variant="h6" color="error.main">
								−{formatAmount(Math.abs(creditNoteFor.total), creditNoteFor.currency)}
							</Typography>
						</Stack>
					</Box>

					<Grid container spacing={2} sx={{ mb: 2 }}>
						<Grid item xs={12} sm={6}>
							<TextField
								fullWidth
								size="small"
								type="date"
								label="Fecha NC"
								InputLabelProps={{ shrink: true }}
								value={issueDate}
								onChange={(e) => setIssueDate(e.target.value)}
							/>
						</Grid>
					</Grid>

					<Divider sx={{ mb: 2 }} />

					<Typography variant="subtitle2" sx={{ mb: 0.5 }}>
						Emisión fiscal (opcional)
					</Typography>
					<Typography variant="caption" color="text.secondary">
						Si ingresás la referencia externa, la NC se emite directamente. La URL del PDF es opcional.
					</Typography>

					<Grid container spacing={2} sx={{ mt: 0.5, mb: 2 }}>
						<Grid item xs={12} sm={6}>
							<TextField
								fullWidth
								size="small"
								label="Referencia externa"
								placeholder="CAE / Timbrado / CUFE"
								value={externalReference}
								onChange={(e) => setExternalReference(e.target.value)}
							/>
						</Grid>

						<Grid item xs={12} sm={6}>
							<TextField
								fullWidth
								size="small"
								label="URL del PDF"
								value={pdfUrl}
								onChange={(e) => setPdfUrl(e.target.value)}
							/>
						</Grid>

						<Grid item xs={12}>
							<TextField
								fullWidth
								size="small"
								label="Notas"
								value={notes}
								onChange={(e) => setNotes(e.target.value)}
								multiline
								minRows={2}
							/>
						</Grid>
					</Grid>

					{error && (
						<Alert severity="error" sx={{ mb: 2 }}>
							{error}
						</Alert>
					)}

					<Stack direction="row" spacing={1} justifyContent="flex-end">
						<Button onClick={onClose} disabled={submitting}>
							Cancelar
						</Button>
						<Button variant="contained" color="error" onClick={handleSubmit} disabled={submitting}>
							Generar NC
						</Button>
					</Stack>
				</Box>
			</Drawer>
		);
	}

	return (
		<Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 640 } } }}>
			<Box sx={{ p: 3, pb: 6 }}>
				<Typography variant="h6" sx={{ mb: 2 }}>
					Nueva factura
				</Typography>

				<Grid container spacing={2} sx={{ mb: 2 }}>
					<Grid item xs={12} sm={6}>
						<FormControl fullWidth size="small">
							<InputLabel>Tipo</InputLabel>
							<Select label="Tipo" value={type} onChange={(e) => handleTypeChange(e.target.value as InvoiceTypeEnum)}>
								<MenuItem value={InvoiceTypeEnum.Credit}>Crédito</MenuItem>
								<MenuItem value={InvoiceTypeEnum.Cash} disabled={receiptsLoading || !contadoAvailable}>
									Contado
									{receiptsLoading && <CircularProgress size={12} sx={{ ml: 1 }} />}
									{!receiptsLoading && !contadoAvailable && (
										<Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
											(sin anticipos)
										</Typography>
									)}
								</MenuItem>
							</Select>
						</FormControl>

						{type === InvoiceTypeEnum.Cash && contadoAvailable && (
							<Typography variant="caption" color="info.main" sx={{ mt: 0.5, display: 'block' }}>
								Anticipos disponibles: {formatAmount(maxContadoAmount, currency)}
							</Typography>
						)}
					</Grid>

					<Grid item xs={12} sm={6}>
						<FormControl fullWidth size="small">
							<InputLabel>Facturar a</InputLabel>
							<Select
								label="Facturar a"
								value={passengerId}
								onChange={(e) => {
									setPassengerId(e.target.value);
									// El vencimiento editado a mano correspondía al cliente
									// anterior: al cambiar de cliente vuelve a mandar su condición.
									setDueDateTouched(false);
								}}
								displayEmpty
								renderValue={(v) =>
									invoiceables.find((p) => p.id === v)?.name ?? (
										<Typography color="text.secondary" fontSize="inherit">
											(sin pasajero facturable)
										</Typography>
									)
								}
							>
								{invoiceables.length === 0 ? (
									<MenuItem disabled value="">
										No hay pasajeros facturables
									</MenuItem>
								) : (
									invoiceables.map((party) => (
										<MenuItem key={party.id} value={party.id}>
											{party.name}
										</MenuItem>
									))
								)}
							</Select>
						</FormControl>
					</Grid>

					<Grid item xs={12} sm={type === InvoiceTypeEnum.Cash ? 6 : 4}>
						<FormControl fullWidth size="small">
							<InputLabel>Moneda</InputLabel>
							<Select label="Moneda" value={currency} onChange={(e) => handleCurrencyChange(e.target.value)}>
								<MenuItem value="USD">USD</MenuItem>
								{agencyId === COLOMBIA_AGENCY_ID && <MenuItem value="COP">COP</MenuItem>}
							</Select>
						</FormControl>
					</Grid>

					{currency !== 'USD' && (
						<Grid item xs={12} sm={4}>
							<TextField
								fullWidth
								size="small"
								type="number"
								label={`TC ${currency}/USD`}
								value={exchangeRate}
								onChange={(e) => setExchangeRate(Number(e.target.value))}
								error={exchangeRate <= 1}
								helperText={
									exchangeRate <= 1
										? `Ingresá cuántos ${currency} equivalen a 1 USD`
										: impliedExchangeRate && impliedExchangeRate !== exchangeRate
										? `Sugerido según servicios: ${impliedExchangeRate}`
										: undefined
								}
								InputProps={{
									startAdornment: <InputAdornment position="start">×</InputAdornment>,
								}}
							/>
						</Grid>
					)}

					<Grid item xs={6} sm={type === InvoiceTypeEnum.Cash ? 6 : 4}>
						<TextField
							fullWidth
							size="small"
							type="date"
							label="Emisión"
							InputLabelProps={{ shrink: true }}
							value={issueDate}
							onChange={(e) => {
								setIssueDate(e.target.value);
								applyDueDateFromCredit(e.target.value);
							}}
						/>
					</Grid>

					{type !== InvoiceTypeEnum.Cash && (
						<Grid item xs={6} sm={4}>
							<TextField
								fullWidth
								size="small"
								type="date"
								label={
									creditRule?.term
										? `Vencimiento (${creditRule.term.name})`
										: corpoCreditDays > 0
										? `Vencimiento (${corpoCreditDays} días)`
										: leisureCreditDueDate
										? 'Vencimiento (inicio de viaje −45 días)'
										: 'Vencimiento'
								}
								InputLabelProps={{ shrink: true }}
								value={dueDate}
								onChange={(e) => {
									setDueDate(e.target.value);
									setDueDateTouched(true);
								}}
							/>
						</Grid>
					)}
				</Grid>

				{/* De dónde salió el vencimiento: la condición aplicada, la
				    divergencia si la editaron a mano, o el aviso de que va a mano. */}
				{type === InvoiceTypeEnum.Credit && (creditRuleLoading || creditRule) && (
					<Box mb={2}>
						<DueDateRuleNotice
							loading={creditRuleLoading}
							resolved={creditRule}
							currentDueDate={dueDate}
							onRestore={() => {
								if (ruleDueDate) {
									setDueDate(ruleDueDate);
									setDueDateTouched(false);
								}
							}}
							passengerHref={passengerId ? `/passengers/${passengerId}?from=customers` : undefined}
						/>
					</Box>
				)}

				{contadoIsCapped && (
					<Alert severity="info" sx={{ mb: 2 }}>
						En <strong>Contado</strong> solo podés facturar hasta el monto cubierto por los anticipos disponibles (
						{formatAmount(maxContadoAmount, currency)}). Los importes fueron ajustados automáticamente. El saldo
						restante ({formatAmount(round2(totalPendingAllServices - maxContadoAmount), currency)}) quedará pendiente y
						podrás facturarlo posteriormente.
					</Alert>
				)}

				{invoiceableBilling?.clientNotes && (
					<Alert severity="info" sx={{ mb: 2, whiteSpace: 'pre-line' }}>
						<strong>Notas del cliente:</strong> {invoiceableBilling.clientNotes}
					</Alert>
				)}

				{billingWarnings.map((warning) => (
					<Alert key={warning} severity="warning" sx={{ mb: 2 }}>
						{warning}
					</Alert>
				))}

				{hasBillableServices && (
					<>
						<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
							<Tooltip title="Carga todos los servicios con saldo pendiente">
								<span>
									<Button
										size="small"
										variant="outlined"
										startIcon={<PlaylistAddCheckIcon />}
										onClick={() => {
											let r = buildRowsFromBillable('pending', currency);
											if (type === InvoiceTypeEnum.Cash && freeReceipts.length > 0) {
												const cap = freeReceipts.reduce((acc, rc) => acc + coverageOfReceipt(rc, currency), 0);
												r = capRowsToAmount(r, cap);
											}
											setRows(r);
											setNotes(buildNotesFromBillable(r));
										}}
										disabled={pendingServiceCount === 0}
									>
										Facturar todo pendiente
										{pendingServiceCount > 0 && (
											<Chip
												label={pendingServiceCount}
												size="small"
												color="primary"
												sx={{ ml: 0.5, height: 18, fontSize: 11 }}
											/>
										)}
									</Button>
								</span>
							</Tooltip>

							{invoiceablesCount > 1 && (
								<Tooltip title={`Divide el monto pendiente en ${invoiceablesCount} partes iguales`}>
									<span>
										<Button
											size="small"
											variant="outlined"
											startIcon={<CallSplitIcon />}
											onClick={() => {
												let r = buildRowsFromBillable('equitative', currency);
												if (type === InvoiceTypeEnum.Cash && freeReceipts.length > 0) {
													const cap = freeReceipts.reduce((acc, rc) => acc + coverageOfReceipt(rc, currency), 0);
													r = capRowsToAmount(r, cap);
												}
												setRows(r);
												setNotes(buildNotesFromBillable(r));
											}}
											disabled={pendingServiceCount === 0}
										>
											Equitativo (1/{invoiceablesCount})
										</Button>
									</span>
								</Tooltip>
							)}
						</Stack>

						<Divider sx={{ mb: 2 }} />
					</>
				)}

				<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
					<Typography variant="subtitle2">Ítems</Typography>
					<Button size="small" startIcon={<AddIcon />} onClick={() => setRows((prev) => [...prev, emptyRow()])}>
						Agregar ítem manual
					</Button>
				</Stack>

				{rows.length === 0 && (
					<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
						No hay servicios. Agregá un ítem manual.
					</Typography>
				)}

				<Stack spacing={1.5}>
					{rows.map((row, index) => {
						const isServiceRow = Boolean(row.serviceId);
						// IVA hacia adentro: el importe cargado ya es el total final del ítem.
						const rowTotal = round2(row.unitPrice);
						const rowBaseRatio = row.taxableBaseRatio ?? 1;
						const rowTaxIncluded =
							row.taxRate > 0
								? rowBaseRatio < 1
									? round2(rowTotal * rowBaseRatio * (row.taxRate / 100))
									: round2(rowTotal - rowTotal / (1 + row.taxRate / 100))
								: 0;
						const pctBilling = row.serviceTotalSaleAmount
							? Math.min(100, Math.round((row.unitPrice / row.serviceTotalSaleAmount) * 100))
							: null;
						const isPartial = pctBilling !== null && pctBilling < 100 && pctBilling > 0;

						return (
							<Box
								key={index}
								sx={{
									border: 1,
									borderColor: row.selected ? 'divider' : 'transparent',
									borderRadius: 1,
									p: 1.5,
									bgcolor: row.selected ? 'background.paper' : 'action.hover',
									opacity: row.fullyBilled && !row.selected ? 0.5 : 1,
								}}
							>
								<Stack direction="row" alignItems="flex-start" spacing={0.5}>
									<Checkbox
										size="small"
										checked={row.selected}
										onChange={(e) => updateRow(index, { selected: e.target.checked })}
										sx={{ mt: -0.5 }}
									/>

									<Box sx={{ flex: 1 }}>
										<Typography variant="body2" sx={{ fontWeight: row.selected ? 600 : 400 }}>
											{row.description || '(sin descripción)'}
										</Typography>

										{row.adenda && (
											<Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
												{row.adenda}
											</Typography>
										)}

										{row.fullyBilled && (
											<Typography variant="caption" color="success.main">
												✓ Ya completamente facturado
											</Typography>
										)}

										{isServiceRow &&
											!row.fullyBilled &&
											row.serviceAlreadyBilled !== undefined &&
											row.serviceAlreadyBilled > 0 && (
												<Typography variant="caption" color="warning.main">
													Ya facturado: {formatAmount(row.serviceAlreadyBilled, currency)}
												</Typography>
											)}
									</Box>

									{!isServiceRow && (
										<Tooltip title="Eliminar ítem">
											<IconButton size="small" onClick={() => setRows((prev) => prev.filter((_, i) => i !== index))}>
												<DeleteOutlineIcon fontSize="small" />
											</IconButton>
										</Tooltip>
									)}
								</Stack>

								{row.selected && (
									<Box sx={{ pl: 4, mt: 1 }}>
										<Grid container spacing={1} alignItems="center">
											<Grid item xs={5} sm={4}>
												<TextField
													fullWidth
													size="small"
													type="number"
													label="Importe"
													value={row.unitPrice}
													onChange={(e) => updateRow(index, { unitPrice: Number(e.target.value) })}
													InputProps={{
														endAdornment: (
															<InputAdornment position="end">
																<Typography variant="caption">{currency}</Typography>
															</InputAdornment>
														),
													}}
												/>
											</Grid>

											<Grid item xs={3} sm={2}>
												<TextField
													fullWidth
													size="small"
													type="number"
													label="IVA %"
													value={row.taxRate}
													onChange={(e) => updateRow(index, { taxRate: Number(e.target.value) })}
												/>
												{row.taxRate > 0 && rowBaseRatio < 1 && (
													<Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
														s/ {Math.round(rowBaseRatio * 100)}% base → efectivo {round2(row.taxRate * rowBaseRatio)}%
													</Typography>
												)}
											</Grid>

											<Grid item xs={4} sm={3}>
												<Typography variant="body2" sx={{ fontWeight: 600 }}>
													= {formatAmount(rowTotal, currency)}
												</Typography>

												{rowTaxIncluded > 0 && (
													<Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
														IVA incl.: {formatAmount(rowTaxIncluded, currency)}
													</Typography>
												)}

												{currency !== 'USD' && exchangeRate > 0 && (
													<Typography variant="caption" color="text.secondary">
														≈ {formatAmount(toMainCurrency(rowTotal, currency, exchangeRate), 'USD')}
													</Typography>
												)}
											</Grid>

											{row.serviceTotalSaleAmount !== undefined && (
												<Grid item xs={12}>
													<Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
														<Typography variant="caption" color="text.secondary">
															Servicio total: {formatAmount(row.serviceTotalSaleAmount, currency)}
														</Typography>

														<Typography
															variant="caption"
															sx={{ fontWeight: 600, color: isPartial ? 'warning.main' : 'success.main' }}
														>
															{pctBilling}% del servicio
														</Typography>
													</Stack>

													<LinearProgress
														variant="determinate"
														value={pctBilling ?? 0}
														color={isPartial ? 'warning' : 'success'}
														sx={{ height: 4, borderRadius: 2 }}
													/>
												</Grid>
											)}
										</Grid>

										{!isServiceRow && (
											<TextField
												fullWidth
												size="small"
												label="Descripción"
												value={row.description}
												onChange={(e) => updateRow(index, { description: e.target.value })}
												sx={{ mt: 1 }}
											/>
										)}
									</Box>
								)}
							</Box>
						);
					})}
				</Stack>

				<Box sx={{ textAlign: 'right', mt: 2 }}>
					<Typography variant="body2" color="text.secondary">
						Subtotal: {formatAmount(totals.subtotal, currency)}
					</Typography>

					<Typography variant="body2" color="text.secondary">
						IVA incluido: {formatAmount(totals.taxAmount, currency)}
					</Typography>

					<Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
						Total: {formatAmount(totals.total, currency)}
					</Typography>

					{currency !== 'USD' && exchangeRate > 0 && (
						<Typography variant="caption" color="text.secondary">
							≈ {formatAmount(toMainCurrency(totals.total, currency, exchangeRate), 'USD')}
						</Typography>
					)}

					{agencyId === COLOMBIA_AGENCY_ID && (
						<Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
							Mercado: {localCurrencyLabel}
						</Typography>
					)}
				</Box>

				{type === InvoiceTypeEnum.Cash && !isCreditNote && (
					<>
						<Divider sx={{ my: 2 }} />

						<Typography variant="subtitle2" sx={{ mb: 1 }}>
							Anticipos a vincular
						</Typography>

						{receiptsLoading ? (
							<CircularProgress size={20} />
						) : freeReceipts.length === 0 ? (
							<Typography variant="body2" color="text.secondary">
								No hay anticipos libres en esta orden.
							</Typography>
						) : (
							<Stack>
								{freeReceipts.map((receipt) => {
									const coverageAmount = coverageOfReceipt(receipt, currency);
									const isDifferentCurrency = normalizeCurrency(receipt.currency) !== normalizeCurrency(currency);

									return (
										<FormControlLabel
											key={receipt.id}
											control={
												<Checkbox
													size="small"
													checked={selectedReceipts.has(receipt.id)}
													onChange={() => toggleReceipt(receipt.id)}
												/>
											}
											label={`${formatAmount(receipt.transactionAmount, receipt.currency)} · ${
												PLATFORM_LABEL[receipt.platformType]
											}${receipt.authorizationCode ? ` · ${receipt.authorizationCode}` : ''}${
												isDifferentCurrency ? ` · Cubre ${formatAmount(coverageAmount, currency)}` : ''
											}`}
										/>
									);
								})}
							</Stack>
						)}

						{selectedReceipts.size > 0 && totals.total > 0 && (
							<Box sx={{ mt: 1 }}>
								<Stack direction="row" justifyContent="space-between">
									<Typography variant="caption" color="text.secondary">
										Cobertura proyectada
									</Typography>
									<Typography
										variant="caption"
										sx={{ fontWeight: 600, color: projectedCoverage >= totals.total ? 'success.main' : 'warning.main' }}
									>
										{formatAmount(projectedCoverage, currency)}
									</Typography>
								</Stack>

								<Stack direction="row" justifyContent="space-between">
									<Typography variant="caption" color="text.secondary">
										Saldo resultante
									</Typography>
									<Typography variant="caption" sx={{ fontWeight: 600 }}>
										{formatAmount(round2(totals.total - projectedCoverage), currency)}
									</Typography>
								</Stack>
							</Box>
						)}
					</>
				)}

				<Divider sx={{ my: 2 }} />

				<Typography variant="subtitle2" sx={{ mb: 0.5 }}>
					Emisión fiscal (opcional)
				</Typography>
				<Typography variant="caption" color="text.secondary">
					Si ingresás la referencia externa, la factura se emite directamente (ISSUED). La URL del PDF es opcional.
				</Typography>

				<Grid container spacing={2} sx={{ mt: 0.5 }}>
					<Grid item xs={12} sm={6}>
						<TextField
							fullWidth
							size="small"
							label="Referencia externa"
							placeholder="CAE / Timbrado / CUFE"
							value={externalReference}
							onChange={(e) => setExternalReference(e.target.value)}
						/>
					</Grid>

					<Grid item xs={12} sm={6}>
						<TextField
							fullWidth
							size="small"
							label="URL del PDF"
							value={pdfUrl}
							onChange={(e) => setPdfUrl(e.target.value)}
						/>
					</Grid>

					<Grid item xs={12}>
						<TextField
							fullWidth
							size="small"
							label="Notas"
							value={notes}
							onChange={(e) => setNotes(e.target.value)}
							multiline
							minRows={2}
						/>
					</Grid>
				</Grid>

				{error && (
					<Alert severity="error" sx={{ mt: 2 }}>
						{error}
					</Alert>
				)}

				<Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 3 }}>
					<Button onClick={onClose} disabled={submitting}>
						Cancelar
					</Button>

					<Button variant="contained" onClick={handleSubmit} disabled={submitting || receiptsLoading}>
						{isCreditNote ? 'Generar NC' : 'Crear factura'}
					</Button>
				</Stack>
			</Box>
		</Drawer>
	);
};
